import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ArrowRight, Zap, Package, CornerDownLeft } from 'lucide-react';
import { WS, SKILL_LIBRARY } from './workspaceData.ts';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  emoji?: string;
  category: string;
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  activeSkillIds: string[];
  onToggleSkill: (id: string) => void;
  onSave: () => void;
  onNavigateBack: () => void;
  setSystemPrompt: (v: string) => void;
}

export default function CommandPalette({
  open, onClose, activeSkillIds, onToggleSkill, onSave, onNavigateBack, setSystemPrompt,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef  = useRef<HTMLDivElement>(null);

  // Build command list
  const allCommands: CommandItem[] = [
    {
      id: 'save', label: 'Save Agent', description: 'Save all changes', emoji: '💾', category: 'Actions',
      action: () => { onSave(); onClose(); },
    },
    {
      id: 'back', label: 'Go to Agents', description: 'Back to agents list', emoji: '⬅️', category: 'Actions',
      action: () => { onNavigateBack(); onClose(); },
    },
    {
      id: 'clear-prompt', label: 'Clear System Prompt', description: 'Remove all prompt text', emoji: '🗑️', category: 'Actions',
      action: () => { setSystemPrompt(''); onClose(); },
    },
    // Add/remove skill commands
    ...SKILL_LIBRARY.slice(0, 20).map(skill => ({
      id: `skill-${skill.id}`,
      label: activeSkillIds.includes(skill.id) ? `Remove ${skill.name}` : `Add ${skill.name}`,
      description: skill.description,
      emoji: skill.emoji,
      category: 'Skills',
      action: () => { onToggleSkill(skill.id); onClose(); },
    })),
  ];

  const filtered = query.trim()
    ? allCommands.filter(c =>
        c.label.toLowerCase().includes(query.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(query.toLowerCase()) ||
        c.category.toLowerCase().includes(query.toLowerCase())
      )
    : allCommands.slice(0, 12);

  // Group by category
  const groups = filtered.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, CommandItem[]>);

  const flatItems = Object.values(groups).flat();

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelected(0);
  }, [query]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, flatItems.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && flatItems[selected]) { flatItems[selected].action(); }
  }, [open, flatItems, selected, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-idx="${selected}"]`) as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  return (
    <div
      className="ws-cmd-palette-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(28,28,26,0.35)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        className="ws-cmd-palette-box"
        onClick={e => e.stopPropagation()}
        style={{
          width: 580, maxWidth: 'calc(100vw - 40px)',
          background: WS.surface2,
          border: `1px solid ${WS.border2}`,
          borderRadius: 16,
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          overflow: 'hidden',
        }}
      >
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px',
          borderBottom: `1px solid ${WS.border}`,
        }}>
          <Search size={16} color={WS.ink4} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search actions, skills, settings…"
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              color: WS.ink,
              fontSize: 14.5,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <kbd style={{
            fontSize: 10, color: WS.ink4,
            background: WS.surface3, border: `1px solid ${WS.border}`,
            padding: '2px 6px', borderRadius: 5, fontFamily: 'inherit',
          }}>
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 400, overflowY: 'auto' }}>
          {flatItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: WS.ink4, fontSize: 13 }}>
              No results for "{query}"
            </div>
          ) : (
            Object.entries(groups).map(([category, items]) => {
              let globalIdx = 0;
              const categoryStartIdx = flatItems.indexOf(items[0]);

              return (
                <div key={category}>
                  <div style={{
                    padding: '8px 14px 4px',
                    fontSize: 10, fontWeight: 600, color: WS.ink4,
                    textTransform: 'uppercase', letterSpacing: '0.07em',
                  }}>
                    {category}
                  </div>
                  {items.map((item, localIdx) => {
                    const idx = categoryStartIdx + localIdx;
                    const isSelected = idx === selected;
                    return (
                      <div
                        key={item.id}
                        data-cmd-idx={idx}
                        onClick={item.action}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 14px', cursor: 'pointer',
                          background: isSelected ? WS.accentBg : 'transparent',
                          transition: 'background 0.1s',
                          borderLeft: `2px solid ${isSelected ? WS.accent : 'transparent'}`,
                        }}
                        onMouseEnter={() => setSelected(idx)}
                      >
                        <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.emoji}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: isSelected ? WS.accentHov : WS.ink }}>
                            {item.label}
                          </div>
                          {item.description && (
                            <div style={{ fontSize: 11, color: WS.ink4, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.description}
                            </div>
                          )}
                        </div>
                        {isSelected && <CornerDownLeft size={12} color={WS.accentHov} />}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer hints */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '8px 16px',
          borderTop: `1px solid ${WS.border}`,
          background: WS.surface,
        }}>
          {[
            { keys: ['↑', '↓'], label: 'Navigate' },
            { keys: ['↵'], label: 'Select' },
            { keys: ['Esc'], label: 'Close' },
          ].map(hint => (
            <div key={hint.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {hint.keys.map(k => (
                <kbd key={k} style={{
                  fontSize: 9.5, color: WS.ink4,
                  background: WS.surface2, border: `1px solid ${WS.border}`,
                  padding: '1px 5px', borderRadius: 4, fontFamily: 'inherit',
                }}>
                  {k}
                </kbd>
              ))}
              <span style={{ fontSize: 10.5, color: WS.ink4 }}>{hint.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
